import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import { createLogger } from "../utils/logger";
import { track, registerSuperProperties, type TokenRefreshTrigger } from "../utils/analytics";
import type { AuthTier } from "../config/authConfig";
import { getPersistedTenantId } from "../config/authConfig";
import {
  initializeMsal,
  acquireTokenInitial,
  acquireTokenSilent,
  signOut as msalSignOut,
  resetMsalState,
  getOfficeTenantId,
} from "../services/msalAuthService";
import { registerAuthRefresh } from "../auth/authRefreshBridge";
import { useLegacySsoFlow } from "../auth/useLegacySsoFlow";
import { fetchUserMe, type AccessDecision, type FeatureEntitlements } from "../api/userMeApi";
import {
  detectAuthTier,
  describeUnsupportedPlatform,
  extractUserInfoFromToken,
  identifyAndEmitAppOpened,
  classifyNaaFailure,
  extractMsalErrorFields,
  type UserInfo,
} from "./authHelpers";

const log = createLogger("AuthContext");

interface AuthContextType {
  accessToken: string | null;
  userInfo: UserInfo | null;
  isLoading: boolean;
  error: string | null;
  authCompleted: boolean;
  allowFallback: boolean;
  /**
   * Result of the bootstrap GET /users/me call against the backend access
   * gate. `null` while the call is in flight (or before a token has been
   * acquired). Consumers gate the loading screen on this becoming non-null
   * (or on the absence of a token), then branch on `kind`.
   */
  accessDecision: AccessDecision | null;
  /**
   * Per-tenant feature entitlements from the bootstrap /users/me call. Non-null
   * only on an `allowed` decision whose body carried a `features` object; `null`
   * otherwise (call in flight, denied/unknown, or a backend that doesn't report
   * entitlements). Consumers read this via `useEntitlements`, which treats `null`
   * or an absent field as "no signal" and falls back to the build-flag default.
   */
  featureEntitlements: FeatureEntitlements | null;
  /**
   * True if the user has a valid token and backend calls are meaningful.
   * Consumers should gate API calls on this — if false (e.g. `allowFallback`
   * is true but `accessToken` is null), every backend call will 401 and the
   * user will see cryptic errors.
   */
  canAccessBackend: boolean;
  /**
   * Which tier is currently serving tokens. **Internal/testing only.** No
   * production consumer should branch on this — all tier-dispatch behavior
   * lives inside AuthContext. Exposed so tests can assert the tier path.
   */
  authTier: AuthTier;
  refreshToken: (trigger?: TokenRefreshTrigger) => Promise<void>;
  isTokenValid: () => boolean;
  /**
   * Returns a token that is safe to send right now. If the current token is
   * at/past the refresh boundary (e.g. the taskpane's webview throttled the
   * auto-refresh timer while hidden), it refreshes first and returns the new
   * token. Call this at request-send time instead of reading `accessToken`
   * directly for long-lived flows (chat, summary, analysis refresh).
   */
  getFreshAccessToken: () => Promise<string | null>;
  /** Re-run the auth flow. Used by the degraded-state "Retry sign-in" button. */
  retrySignIn: () => Promise<void>;
  /** Clear local MSAL cache + React state. Does NOT sign the user out of Office. */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Refresh this long before expiry. Shared by the proactive auto-refresh timer
 * and the send-time preflight in getFreshAccessToken, so both agree on when a
 * token stops being safe to send.
 */
const TOKEN_REFRESH_BUFFER_MS = 60_000;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [accessToken, setAccessTokenState] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [authCompleted, setAuthCompleted] = useState(false);
  const [allowFallback, setAllowFallback] = useState(false);
  // Bootstrap access gate decision. Stays null until /users/me returns; the
  // loading screen is gated on this becoming non-null so unprovisioned users
  // never see the Welcome screen flash.
  const [accessDecision, setAccessDecision] = useState<AccessDecision | null>(null);
  // State+ref mirror: state triggers re-renders, ref gives synchronous access
  // inside callbacks. All writes go through `setAuthTierBoth`.
  const [authTier, setAuthTier] = useState<AuthTier>("none");
  const timeoutsRef = useRef<Set<NodeJS.Timeout>>(new Set());
  // In-flight guard for refreshToken(). The auto-refresh timer, the 401-bridge,
  // and a cross-tier cascade can all race — dedup converges them on one MSAL call.
  const refreshPromiseRef = useRef<Promise<void> | null>(null);
  const authTierRef = useRef<AuthTier>("none");
  // Email of the currently-authenticated user. Used to detect account switches
  // on the next token. Ref because it's only read during token mutations.
  const currentEmailRef = useRef<string | null>(null);
  // State+ref mirror for the token: getFreshAccessToken must read the value a
  // just-awaited refreshToken() wrote WITHOUT waiting for a re-render, so the
  // ref is written synchronously alongside the state.
  const accessTokenRef = useRef<string | null>(null);
  const setAccessToken = useCallback((token: string | null) => {
    accessTokenRef.current = token;
    setAccessTokenState(token);
    // Register the tenant as a super property so every analytics event can be
    // cut per tenant. Done on every token write (covers both tiers and all
    // refresh paths in one place); posthog.register is an idempotent local set.
    if (token) {
      try {
        const { tid } = jwtDecode<{ tid?: string }>(token);
        if (tid) registerSuperProperties({ tenant_id: tid });
      } catch {
        /* telemetry decode failure is never fatal */
      }
    }
  }, []);

  const isLoading = !authCompleted;
  const canAccessBackend = authCompleted && accessToken !== null;

  /**
   * Single entry point for tier state changes. Writes BOTH the React state
   * (re-renders) AND the ref (synchronously visible). Every call site must go
   * through this — ad-hoc writes risk ref/state drift that corrupts telemetry.
   */
  const setAuthTierBoth = useCallback((tier: AuthTier) => {
    setAuthTier(tier);
    authTierRef.current = tier;
    registerSuperProperties({ auth_tier: tier });
  }, []);

  /**
   * Detect an account switch: new token's email differs from last-seen.
   * Emits PostHog event + logs (PII-safe). Returns true if a switch occurred.
   */
  const detectAccountSwitch = useCallback((newEmail: string): boolean => {
    const previousEmail = currentEmailRef.current;
    currentEmailRef.current = newEmail || null;
    if (!previousEmail || !newEmail) return false;
    if (previousEmail.toLowerCase() === newEmail.toLowerCase()) return false;
    log.warn("Account switch detected — clearing cached state", {
      previousHadEmail: true,
      newHasEmail: true,
    });
    track("auth:account_switched", {});
    return true;
  }, []);

  const { getAccessToken } = useLegacySsoFlow({
    setError,
    setAccessToken,
    setAuthCompleted,
    setUserInfo,
    setAllowFallback,
    setAuthTierBoth,
    detectAccountSwitch,
    timeoutsRef,
  });

  const isTokenValid = useCallback((): boolean => {
    if (!accessToken) return false;
    try {
      const decoded = jwtDecode<{ exp?: number }>(accessToken);
      if (!decoded.exp) return true;
      const bufferMs = 5 * 60 * 1000;
      return Date.now() < decoded.exp * 1000 - bufferMs;
    } catch {
      return false;
    }
  }, [accessToken]);

  const initializeNaaAuth = useCallback(async (): Promise<string | null> => {
    try {
      setError(null);
      log.info("Starting NAA authentication via MSAL");

      const pca = await initializeMsal();
      if (!pca) {
        log.warn("MSAL initialization failed, falling back to Legacy SSO");
        track("auth:naa_fallback", {
          reason: "msal_init_failed",
          from_tier: "naa",
          to_tier: "legacy_sso",
          tenant_id: getOfficeTenantId() ?? getPersistedTenantId() ?? undefined,
        });
        setAuthTierBoth("legacy_sso");
        return await getAccessToken();
      }

      const result = await acquireTokenInitial();
      const token = result.accessToken;
      const { name, email } = extractUserInfoFromToken(token);
      detectAccountSwitch(email);
      setAccessToken(token);
      setAuthCompleted(true);
      setUserInfo({ name, email });

      const decoded = jwtDecode<{ exp?: number }>(token);
      log.info("NAA authentication successful", {
        hasEmail: Boolean(email),
        expiresIn: decoded.exp ? new Date(decoded.exp * 1000).toISOString() : "N/A",
      });
      identifyAndEmitAppOpened({ name, email }, "naa", decoded.exp);
      return token;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      // MSAL's NAA bridge errors (notably Word-on-Windows) arrive with empty
      // message/errorCode, so `errorMessage` is blank. `msalErrorName` (the
      // error's class name, e.g. ServerError) is the field that survives and
      // lets us tell a host-broker refusal apart from other failures.
      const msalFields = extractMsalErrorFields(error);
      log.error("NAA authentication failed, falling back to Legacy SSO", {
        error: errorMessage,
        ...msalFields,
      });
      track("auth:naa_fallback", {
        reason: classifyNaaFailure(error),
        from_tier: "naa",
        to_tier: "legacy_sso",
        error_message: errorMessage,
        correlation_id: msalFields.correlationId,
        msal_error_code: msalFields.msalErrorCode,
        msal_sub_error: msalFields.msalSubError,
        msal_error_name: msalFields.msalErrorName,
        // Which tenant fell back matters as much as why: for
        // msal_interaction_required this is the live list of tenants still
        // missing admin consent. The Office context is the reliable source
        // here - the tenant_id super property is only registered on a token
        // write, which has not happened yet when the first NAA attempt fails.
        tenant_id: getOfficeTenantId() ?? getPersistedTenantId() ?? undefined,
      });
      setAuthTierBoth("legacy_sso");
      return await getAccessToken();
    }
  }, [getAccessToken, setAuthTierBoth, detectAccountSwitch]);

  /**
   * Token refresh. Called from three orthogonal places — intentionally
   * redundant for defense in depth:
   *   1. Auto-refresh timer at `exp - 60s` (proactive, smooth UX).
   *   2. 401 bridge in `errorHandler.ts` (reactive, covers clock skew + server-
   *      side invalidation).
   *   3. Manual consumer call (exposed via useAuth; unused today).
   *
   * TODO (P3): thread a `claims` parameter through for MFA step-up. When the
   * backend returns 401 with `WWW-Authenticate: ... claims="..."`, callers
   * should pass those claims so AAD re-prompts for MFA.
   * `msalAuthService.acquireTokenSilent({ claims })` already accepts them.
   */
  const refreshToken = useCallback(
    async (trigger: TokenRefreshTrigger = "manual"): Promise<void> => {
      // Concurrent callers converge on the in-flight refresh; only the first
      // caller's trigger is reported (good enough - the point is knowing which
      // path keeps tokens alive in the field, not exact attribution per racer).
      if (refreshPromiseRef.current) return refreshPromiseRef.current;

      // Do NOT flip authCompleted/allowFallback during refresh. The path must
      // keep the current token + UI visible while the new token is fetched;
      // flipping authCompleted=false would unmount the whole app behind the
      // loading spinner. If refresh fails, initializeNaaAuth / getAccessToken
      // themselves manage the degraded state.
      const run = async (): Promise<void> => {
        // Snapshot the tier BEFORE the attempt: on total failure the flows
        // mutate the tier to "none" before returning, and the event must name
        // the tier whose refresh failed, not the post-degradation state.
        const tierBefore = authTierRef.current;

        if (tierBefore === "naa") {
          try {
            const result = await acquireTokenSilent();
            setAccessToken(result.accessToken);
            track("auth:token_refreshed", { trigger, auth_tier: "naa" });
            log.info("NAA token refreshed silently");
          } catch (error: unknown) {
            log.warn("NAA silent refresh failed, trying full re-auth", {
              error: error instanceof Error ? error.message : String(error),
            });
            // Report the OUTCOME of the whole refresh operation: a full
            // re-auth that recovers the session is a successful (non-silent)
            // refresh, not a failure - otherwise recovered refreshes would
            // read as token loss in dashboards.
            const token = await initializeNaaAuth();
            if (token) {
              track("auth:token_refreshed", { trigger, auth_tier: "naa", via_full_reauth: true });
            } else {
              track("auth:token_refresh_failed", {
                trigger,
                auth_tier: "naa",
                error_message: error instanceof Error ? error.message : String(error),
              });
            }
          }
        } else {
          const token = await getAccessToken();
          if (token) {
            track("auth:token_refreshed", { trigger, auth_tier: tierBefore });
          } else {
            track("auth:token_refresh_failed", { trigger, auth_tier: tierBefore });
          }
        }
      };

      const promise = run().finally(() => {
        if (refreshPromiseRef.current === promise) refreshPromiseRef.current = null;
      });
      refreshPromiseRef.current = promise;
      return promise;
    },
    [getAccessToken, initializeNaaAuth]
  );

  /**
   * Send-time preflight: return a token that is safe to use right now.
   *
   * The proactive timer below normally refreshes at `exp - buffer`, but the
   * taskpane's webview throttles/suspends timers while hidden (observed on
   * Word Desktop Windows), so a user can come back to an expired token and
   * their first request fails in their face. Checking at send time closes
   * that gap. A failed refresh falls back to the current token - the 401
   * bridge remains the safety net.
   */
  const getFreshAccessToken = useCallback(async (): Promise<string | null> => {
    const current = accessTokenRef.current;
    if (!current) return null;
    try {
      const decoded = jwtDecode<{ exp?: number }>(current);
      if (decoded.exp && Date.now() >= decoded.exp * 1000 - TOKEN_REFRESH_BUFFER_MS) {
        log.info("Token at/past refresh boundary at send time, refreshing before request");
        await refreshToken("send_preflight");
      }
    } catch (error) {
      log.warn("Send-time token refresh failed, proceeding with current token", {
        error: error instanceof Error ? error.message : String(error),
      });
    }
    return accessTokenRef.current;
  }, [refreshToken]);

  // Register refreshToken with the module-level bridge so errorHandler.ts can
  // trigger a refresh on 401/403 without importing AuthContext.
  useEffect(() => {
    const unregister = registerAuthRefresh(() => refreshToken("http_401"));
    return unregister;
  }, [refreshToken]);

  // Auto-refresh token shortly before it expires. For BOTH tiers:
  //  - NAA: acquireTokenSilent hits MSAL's cache or silently refreshes. Without
  //    this the React-state `accessToken` goes stale even though MSAL's cache
  //    stays fresh, so hook consumers would send expired tokens.
  //  - Legacy SSO: re-fetches via Office.auth.getAccessToken.
  useEffect(() => {
    if (!accessToken) return;

    try {
      const decoded = jwtDecode<{ exp?: number }>(accessToken);
      if (!decoded.exp) return;

      const expiryMs = decoded.exp * 1000;
      const refreshAtMs = expiryMs - TOKEN_REFRESH_BUFFER_MS;
      const delayMs = refreshAtMs - Date.now();

      if (delayMs <= 0) {
        // Token already at/past refresh boundary (e.g. browser suspended).
        // setTimeout(0) so we don't refresh synchronously and create a loop.
        log.info("Token already past refresh boundary, refreshing immediately");
        const id = setTimeout(() => refreshToken("timer"), 0);
        return () => clearTimeout(id);
      }

      const id = setTimeout(() => {
        log.info("Token about to expire, refreshing automatically");
        refreshToken("timer");
      }, delayMs);
      return () => clearTimeout(id);
    } catch (error) {
      log.warn("Failed to schedule token refresh", { error });
    }
  }, [accessToken, refreshToken]);

  // The full auth flow — extracted so `retrySignIn` can re-run it without
  // remounting the provider. Emits auth:session_started/_completed so the
  // denominator for health dashboards is accurate on retries too.
  const runInitialAuth = useCallback(async () => {
    if (typeof Office === "undefined" || !Office.context) {
      log.warn("Office context not available, skipping auth init");
      return;
    }
    log.info("Office context available, starting authentication", {
      host: Office.context.host,
      platform: Office.context.platform,
    });

    const tier = detectAuthTier();
    setAuthTierBoth(tier);
    log.info("Auth tier detected", { tier });

    // Office host/platform as super properties: every event (not just auth
    // ones) becomes splittable by Word Desktop vs Word Online vs Mac.
    //
    // office_version and has_markup_mode_api answer a question we currently
    // cannot: how much of the user base runs a build new enough to tell us how
    // Word is displaying tracked changes. That display setting silently breaks
    // redline apply (see #332), and the API that exposes it needs
    // WordApiDesktop 1.4 - Word on Windows 2508 or Mac 16.100.4 and later, and
    // never on the web or on volume-licensed builds. Both reads are synchronous
    // property lookups on an already-initialized context, so they cost nothing,
    // and both fall back rather than throwing on hosts that lack them.
    registerSuperProperties({
      office_host: String(Office.context.host),
      office_platform: String(Office.context.platform),
      office_version: Office.context.diagnostics?.version ?? "unknown",
      has_markup_mode_api:
        Office.context.requirements?.isSetSupported?.("WordApiDesktop", "1.4") ?? false,
    });

    const sessionStartMs = Date.now();
    track("auth:session_started", {
      detected_tier: tier,
      host: String(Office.context.host),
      platform: String(Office.context.platform),
    });

    const emitCompleted = (finalTier: AuthTier, success: boolean) => {
      track("auth:session_completed", {
        detected_tier: tier,
        final_tier: finalTier,
        success,
        duration_ms: Date.now() - sessionStartMs,
      });
    };

    if (tier === "naa") {
      const token = await initializeNaaAuth();
      emitCompleted(authTierRef.current, token !== null);
    } else if (tier === "legacy_sso") {
      const token = await getAccessToken();
      emitCompleted("legacy_sso", token !== null);
    } else {
      log.warn("No supported auth tier available on this platform");
      track("auth:unsupported_platform", {
        host: String(Office.context.host),
        platform: String(Office.context.platform),
      });
      setError(describeUnsupportedPlatform());
      setAuthCompleted(true);
      setAllowFallback(true);
      emitCompleted("none", false);
    }
  }, [getAccessToken, initializeNaaAuth, setAuthTierBoth]);

  const retrySignIn = useCallback(async () => {
    log.info("User requested sign-in retry");
    setError(null);
    setAuthCompleted(false);
    setAllowFallback(false);
    setAccessDecision(null);
    await runInitialAuth();
  }, [runInitialAuth]);

  /**
   * Local sign-out. Clears MSAL cache + React state. Does NOT sign the user
   * out of Office itself — the next add-in load will re-auth via silent flow
   * if the Office host session is still active.
   */
  const signOut = useCallback(async () => {
    log.info("Local sign-out requested");
    await msalSignOut();
    resetMsalState();
    setAccessToken(null);
    setUserInfo(null);
    setAuthCompleted(false);
    setAllowFallback(false);
    setError(null);
    setAccessDecision(null);
    setAuthTierBoth("none");
    currentEmailRef.current = null;
  }, [setAuthTierBoth]);

  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout | null = null;
    const tryInit = () => {
      if (!mounted) return;
      if (typeof Office !== "undefined" && Office.context) {
        runInitialAuth();
      } else {
        log.warn("Office context not available, retrying in 1 second");
        timeoutId = setTimeout(tryInit, 1000);
      }
    };
    tryInit();
    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current.clear();
    };
  }, [runInitialAuth]);

  // Bootstrap access check. Fires once after a token is acquired; the result
  // gates the loading screen → Welcome / sad-path handoff. Token refresh
  // (which only updates `accessToken`, not the decision) intentionally does
  // NOT re-fire this — the gate decision doesn't change with each token
  // refresh, only on cold start or explicit retry/sign-out.
  useEffect(() => {
    if (!accessToken || accessDecision !== null) return;
    let cancelled = false;
    fetchUserMe(accessToken).then((decision) => {
      if (cancelled) return;
      setAccessDecision(decision);
      log.info("Bootstrap access decision", {
        kind: decision.kind,
        ...(decision.kind === "denied" ? { status: decision.status } : {}),
        ...(decision.kind === "unknown" ? { reason: decision.reason } : {}),
      });
    });
    return () => {
      cancelled = true;
    };
  }, [accessToken, accessDecision]);

  // Surface the per-tenant entitlements from the access decision so feature
  // gating doesn't have to re-derive them from `accessDecision.kind`. Only an
  // `allowed` decision carries entitlements; everything else is "no signal"
  // (null) and consumers fall back to build flags.
  const featureEntitlements: FeatureEntitlements | null =
    accessDecision?.kind === "allowed" ? (accessDecision.features ?? null) : null;

  const contextValue = {
    accessToken,
    userInfo,
    isLoading,
    error,
    authCompleted,
    allowFallback,
    accessDecision,
    featureEntitlements,
    canAccessBackend,
    authTier,
    refreshToken,
    isTokenValid,
    getFreshAccessToken,
    retrySignIn,
    signOut,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
